/**
 * Cookie Consent Hook
 * GDPR & ePrivacy compliant cookie consent management
 */

import { useState, useCallback } from 'react';

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// Cookie preference categories
export interface CookiePreferences {
  necessary: boolean; // Always true, cannot be disabled
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

export interface CookieConsentState {
  hasConsented: boolean;
  preferences: CookiePreferences;
  consentDate: string | null;
}

type StoredConsent = CookieConsentState & { expiryDate?: string };

const COOKIE_CONSENT_KEY = 'cookie_consent';
const CONSENT_EXPIRY_DAYS = 365; // 12 months

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
};

const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const setCookieValue = (name: string, value: string, days: number): void => {
  if (typeof document === 'undefined') return;
  const maxAge = Math.max(0, Math.floor(days * 24 * 60 * 60));
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
};

const deleteCookieValue = (name: string): void => {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
};

const parseStoredConsent = (raw: string | null): StoredConsent | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredConsent;
  } catch {
    return null;
  }
};

const normalizeConsent = (parsed: StoredConsent): CookieConsentState => ({
  hasConsented: !!parsed.hasConsented,
  preferences: parsed.preferences || defaultPreferences,
  consentDate: parsed.consentDate ?? null,
});

const isExpired = (expiryDate?: string): boolean => {
  if (!expiryDate) return false;
  const expiry = new Date(expiryDate);
  return expiry < new Date();
};

const getStoredConsent = (): CookieConsentState | null => {
  // 1) Prefer real browser cookie (persists even if localStorage is cleared on logout)
  const cookieRaw = getCookieValue(COOKIE_CONSENT_KEY);
  const cookieParsed = parseStoredConsent(cookieRaw);

  if (cookieParsed) {
    if (isExpired(cookieParsed.expiryDate)) {
      deleteCookieValue(COOKIE_CONSENT_KEY);
      return null;
    }
    return normalizeConsent(cookieParsed);
  }

  // 2) Fallback: legacy localStorage (and migrate it into a cookie)
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) return null;

    const parsed = parseStoredConsent(stored);
    if (!parsed) {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      return null;
    }

    if (isExpired(parsed.expiryDate)) {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      return null;
    }

    // migrate -> cookie
    setCookieValue(COOKIE_CONSENT_KEY, JSON.stringify(parsed), CONSENT_EXPIRY_DAYS);
    return normalizeConsent(parsed);
  } catch {
    return null;
  }
};

// Update Google Analytics consent mode
const updateGtagConsent = (preferences: CookiePreferences): void => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'analytics_storage': preferences.analytics ? 'granted' : 'denied',
      'ad_storage': preferences.marketing ? 'granted' : 'denied',
      'ad_user_data': preferences.marketing ? 'granted' : 'denied',
      'ad_personalization': preferences.marketing ? 'granted' : 'denied',
    });
  }
};

const saveConsent = (state: CookieConsentState): void => {
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);

  const dataToStore: StoredConsent = {
    ...state,
    expiryDate: expiryDate.toISOString(),
  };

  // Save primarily to cookie (what users expect), keep localStorage as a safe fallback
  setCookieValue(COOKIE_CONSENT_KEY, JSON.stringify(dataToStore), CONSENT_EXPIRY_DAYS);
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(dataToStore));
  } catch {
    // ignore
  }

  // Update Google Analytics consent mode
  updateGtagConsent(state.preferences);
};

export const useCookieConsent = () => {
  const [state, setState] = useState<CookieConsentState>(() => {
    const stored = getStoredConsent();
    return stored || {
      hasConsented: false,
      preferences: defaultPreferences,
      consentDate: null,
    };
  });
  
  // Initialize showBanner based on stored consent - not relying on useEffect
  const [showBanner, setShowBanner] = useState(() => {
    const stored = getStoredConsent();
    return !stored;
  });
  const [showPreferences, setShowPreferences] = useState(false);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    const newState: CookieConsentState = {
      hasConsented: true,
      preferences: {
        necessary: true,
        analytics: true,
        marketing: true,
        functional: true,
      },
      consentDate: new Date().toISOString(),
    };
    
    setState(newState);
    saveConsent(newState);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  // Reject non-essential cookies
  const rejectNonEssential = useCallback(() => {
    const newState: CookieConsentState = {
      hasConsented: true,
      preferences: {
        necessary: true,
        analytics: false,
        marketing: false,
        functional: false,
      },
      consentDate: new Date().toISOString(),
    };
    
    setState(newState);
    saveConsent(newState);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  // Save custom preferences
  const savePreferences = useCallback((preferences: CookiePreferences) => {
    const newState: CookieConsentState = {
      hasConsented: true,
      preferences: {
        ...preferences,
        necessary: true, // Always true
      },
      consentDate: new Date().toISOString(),
    };
    
    setState(newState);
    saveConsent(newState);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  // Open preferences modal
  const openPreferences = useCallback(() => {
    setShowPreferences(true);
  }, []);

  // Close preferences modal
  const closePreferences = useCallback(() => {
    setShowPreferences(false);
    // If user hasn't consented yet, show banner again
    if (!state.hasConsented) {
      setShowBanner(true);
    }
  }, [state.hasConsented]);

  // Check if a specific cookie category is allowed
  const isAllowed = useCallback((category: keyof CookiePreferences): boolean => {
    if (category === 'necessary') return true;
    return state.hasConsented && state.preferences[category];
  }, [state]);

  return {
    state,
    showBanner,
    showPreferences,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
    isAllowed,
  };
};
